/**
 * Toast notification component
 */

import React from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { clsx } from 'clsx';
import { useToastStore, Toast as ToastType } from '../../stores/toast';

export function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  return (
    <div className="fixed top-20 right-4 z-[11000] space-y-2" aria-live="polite">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}

function Toast({ toast, onClose }: { toast: ToastType; onClose: () => void }) {
  const icons = {
    success: <CheckCircle className="h-5 w-5 text-green-500" />,
    error: <AlertCircle className="h-5 w-5 text-red-500" />,
    warning: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
  };

  // Solid opaque backgrounds so content behind never shows through
  const bgColors = {
    success: 'bg-white dark:bg-gray-900 border-green-300 dark:border-green-700',
    error: 'bg-white dark:bg-gray-900 border-red-300 dark:border-red-700',
    warning: 'bg-white dark:bg-gray-900 border-amber-300 dark:border-amber-700',
    info: 'bg-white dark:bg-gray-900 border-blue-300 dark:border-blue-700',
  };

  // Show message once: if only message provided, show it as primary; if title and message both, show both (unless same to avoid duplicate)
  const hasTitle = toast.title != null && toast.title !== '';
  const hasMessage = toast.message != null && toast.message !== '';
  const sameAsTitle = hasTitle && hasMessage && toast.message === toast.title;

  return (
    <div
      className={clsx(
        'flex items-start gap-3 p-4 rounded-lg border-2 shadow-xl max-w-md min-w-[280px] isolate',
        'bg-opacity-100 backdrop-blur-0',
        bgColors[toast.type]
      )}
    >
      {icons[toast.type]}
      <div className="flex-1 min-w-0">
        {hasTitle && <p className="font-semibold text-theme-text break-words">{toast.title}</p>}
        {hasMessage && !sameAsTitle && (
          <p className={clsx('text-theme-text break-words', hasTitle ? 'text-sm text-theme-text-secondary mt-1' : 'font-medium')}>
            {toast.message}
          </p>
        )}
      </div>
      <button onClick={onClose} className="text-theme-text-secondary hover:text-theme-text">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}


